/* eslint-disable react/no-deprecated */
import React from 'react';
import { hydrate } from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import window from 'global/window';
import { matchPath } from 'react-router';
import document from 'global/document';
import loadable from '@loadable/component';
import logger from 'loglevel';
import { generateStore } from './app/store/store';
import {
  PRELOADED_STATE_KEY, AWS_ENV_VARS, VIRTUAL_DIRECTORY, MAINTENANCE_MODE, ENVIRONMENT,
} from './app/common/constants';
import ClientComponentLoader from './app/common/clientComponentLoader';
import { EnvProvider } from './app/common/envContext';
import { defaultRoutes, maintenanceRoutes } from './app/common/routerConfig';
import { getEnvironmentConstant } from './app/common/utilities';

const App = loadable(() => import(/* webpackChunkName: "App" */'./App'));

let preloadedState = {};
// Grab the state from a global variable injected into the server-generated HTML
preloadedState = window[PRELOADED_STATE_KEY];

logger.log('preloadedState', preloadedState);

const defaultEnvData = {};
Object.keys(AWS_ENV_VARS).forEach((key) => {
  // CRA only exposes env vars prefixed with REACT_APP_ to the browser
  // Check both REACT_APP_<KEY> and <KEY> for flexibility
  const reactAppKey = `REACT_APP_${key}`;
  const envValue = process.env[reactAppKey] || process.env[key];

  if (typeof envValue !== 'undefined' && AWS_ENV_VARS[key].validator) {
    defaultEnvData[key] = AWS_ENV_VARS[key].validator(envValue)
      ? envValue
      : AWS_ENV_VARS[key].default;
  } else if (typeof envValue !== 'undefined') {
    defaultEnvData[key] = envValue;
  } else {
    defaultEnvData[key] = AWS_ENV_VARS[key].default;
  }
});
const env = preloadedState?.envData || defaultEnvData;
env.COMPONENT_LOADER = ClientComponentLoader;

// Allow this to be dynamic from env props
// eslint-disable-next-line camelcase,no-undef
__webpack_public_path__ = `${getEnvironmentConstant({ key: VIRTUAL_DIRECTORY, env: env[ENVIRONMENT] })}/`;
// __webpack_public_path__ = `${getEnvironmentConstant({ key: VIRTUAL_DIRECTORY, env: env[ENVIRONMENT] })?.length ? `${getEnvironmentConstant({ key: VIRTUAL_DIRECTORY, env: env[ENVIRONMENT] }) }/` : ''}`;

logger.info('initializing app');

// Create Redux store with initial state
const store = generateStore(preloadedState);

const routes = env[MAINTENANCE_MODE] ? maintenanceRoutes : defaultRoutes;

const hydrateApplication = () => {
  document.getElementById('root').style.minHeight = `${document.getElementById('root').offsetHeight}px`;
  hydrate(
    <HelmetProvider>
      <Provider store={store}>
        <BrowserRouter basename={getEnvironmentConstant({ key: VIRTUAL_DIRECTORY, env: env[ENVIRONMENT] })}>
          <EnvProvider value={env}>
            <App />
          </EnvProvider>
        </BrowserRouter>
      </Provider>
    </HelmetProvider>,
    document.getElementById('root'),
  );
};

const filtered = routes.filter((route) => matchPath(document.location.pathname, {
  path: getEnvironmentConstant({ key: VIRTUAL_DIRECTORY, env: env[ENVIRONMENT] }) + route.path,
  exact: route.exact || false,
  strict: route.strict || false,
}) !== null);

// logger.info('Filtered: ', filtered);

if (filtered.length) {
  // Found a matching component for the route.  Wait for it to load before attempting hydration

  logger.info('Preloading component: ', filtered[0].componentKey);
  ClientComponentLoader[filtered[0].componentKey].load().then(() => {
    hydrateApplication();
  });
} else {
  hydrateApplication();
}
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister();
